// src/services/apiClient.js
import { refreshToken as refreshTokenRequest } from './authService';
import config from '../config';

const BASE_URL = config.apiUrl;

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';

// Refresh proactively this long before the access token's `exp` claim, so an
// in-flight request never races an already-expired token.
const SILENT_REFRESH_BUFFER_MS = 2 * 60 * 1000;

/** Reads the `exp` claim (ms since epoch) out of a JWT without verifying its signature. */
function getTokenExpiryMs(token) {
    try {
        const payload = token.split('.')[1];
        const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
        const decoded = decodeURIComponent(
            atob(padded)
                .split('')
                .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
                .join('')
        );
        const { exp } = JSON.parse(decoded);
        return typeof exp === 'number' ? exp * 1000 : null;
    } catch (e) {
        return null;
    }
}

class ApiClient {
    constructor() {
        this.isRefreshing = false;
        this.failedQueue = [];
        this.silentRefreshTimer = null;

        this.scheduleSilentRefresh();
    }

    setTokens(accessToken, refreshToken) {
        if (accessToken) {
            localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
        }
        if (refreshToken) {
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
        }
        this.scheduleSilentRefresh();
    }

    clearTokens() {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        if (this.silentRefreshTimer) {
            clearTimeout(this.silentRefreshTimer);
            this.silentRefreshTimer = null;
        }
    }

    scheduleSilentRefresh() {
        if (this.silentRefreshTimer) {
            clearTimeout(this.silentRefreshTimer);
            this.silentRefreshTimer = null;
        }

        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (!token) {
            return;
        }

        const expiryMs = getTokenExpiryMs(token);
        if (!expiryMs) {
            return;
        }

        const delay = expiryMs - Date.now() - SILENT_REFRESH_BUFFER_MS;
        if (delay <= 0) {
            this.refreshTokens().catch(() => {});
            return;
        }

        this.silentRefreshTimer = setTimeout(() => {
            this.refreshTokens().catch(() => {});
        }, delay);
    }

    processQueue(error, token = null) {
        this.failedQueue.forEach((promise) => {
            if (error) {
                promise.reject(error);
            } else {
                promise.resolve(token);
            }
        });

        this.failedQueue = [];
    }

    /**
     * Single-flight token refresh. Both the reactive (401-triggered) path and the
     * proactive silent-refresh timer funnel through here, so only one refresh request
     * is ever in flight - concurrent callers just await the same promise.
     */
    refreshTokens() {
        if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
                this.failedQueue.push({ resolve, reject });
            });
        }

        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
            this.handleSessionExpired();
            return Promise.reject(new Error('No refresh token available'));
        }

        this.isRefreshing = true;

        return refreshTokenRequest(refreshToken)
            .then((data) => {
                this.setTokens(data.accessToken, data.refreshToken);
                this.processQueue(null, data.accessToken);
                return data.accessToken;
            })
            .catch((error) => {
                this.processQueue(error, null);
                this.handleSessionExpired();
                throw error;
            })
            .finally(() => {
                this.isRefreshing = false;
            });
    }

    handleSessionExpired() {
        this.clearTokens();
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
            window.location.href = '/login?sessionExpired=1';
        }
    }

    buildHeaders(options) {
        const isFormData = options.body instanceof FormData;
        const defaultHeaders = isFormData ? {} : { 'Content-Type': 'application/json' };
        const headers = { ...defaultHeaders, ...options.headers };

        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }

    async request(url, options = {}) {
        const config = { ...options, headers: this.buildHeaders(options) };
        const response = await fetch(`${BASE_URL}${url}`, config);

        // Never try to refresh on behalf of the auth endpoints themselves - that would
        // just loop on a 401 from a bad login/refresh attempt.
        if (response.status !== 401 || url === '/auth/refresh' || url === '/auth/login') {
            return response;
        }

        if (!localStorage.getItem(REFRESH_TOKEN_KEY)) {
            this.handleSessionExpired();
            return response;
        }

        try {
            await this.refreshTokens();
        } catch (refreshError) {
            // Session already cleared and redirect triggered by refreshTokens/handleSessionExpired.
            return response;
        }

        return fetch(`${BASE_URL}${url}`, { ...config, headers: this.buildHeaders(options) });
    }

    async get(url, options = {}) {
        return this.request(url, { ...options, method: 'GET' });
    }

    async post(url, data, options = {}) {
        return this.request(url, {
            ...options,
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async put(url, data, options = {}) {
        return this.request(url, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async delete(url, options = {}) {
        return this.request(url, { ...options, method: 'DELETE' });
    }

    async patch(url, data, options = {}) {
        return this.request(url, {
            ...options,
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }
}

export default new ApiClient();
